import type {
  ProcessedMessage,
  ProcessedPart,
} from '../utils/message-processing';
import {
  getToolCallIdFromPart,
  getToolNameFromPart,
  getToolStatusFromPart,
  isRunningPart,
} from './chat-message-part-accessors';
import { buildActivityCountSummary } from './chat-message-list-consolidation';
import type {
  RenderablePart,
  SubAgentExecutionGroup,
} from './chat-message-list-types';
import type { GetBackgroundExecutionByToolCallId } from './hitl-types';
import { getToolConfig } from './tool-config';
import {
  getLastCodeAnalystWriteupKey,
  shouldNestSubAgentAssistantPart,
} from './chat-message-list-subagent-text';

export function isAssistantResponsePart(processedPart: ProcessedPart): boolean {
  return (
    processedPart.kind === 'assistant-text' &&
    processedPart.part.type !== 'reasoning'
  );
}

export function isSubAgentPart(processedPart: ProcessedPart): boolean {
  const toolName = getToolNameFromPart(processedPart);

  return toolName ? getToolConfig(toolName).cardType === 'sub-agent' : false;
}

function isNestedInvestigationPart(processedPart: ProcessedPart): boolean {
  const toolName = getToolNameFromPart(processedPart);
  const normalizedToolName = toolName?.replace(/_/g, '-').toLowerCase();

  return (
    normalizedToolName === 'trace-investigation-tool' ||
    normalizedToolName === 'trace-investigation'
  );
}

export function canNestInvestigationInSubAgent({
  group,
  item,
}: {
  group: SubAgentExecutionGroup;
  item: RenderablePart;
}): boolean {
  const parentToolName = getToolNameFromPart(group.parent.processedPart)
    ?.replace(/_/g, '-')
    .toLowerCase();

  return (
    parentToolName === 'application-data-analyst' &&
    isNestedInvestigationPart(item.processedPart)
  );
}

function isBackgroundChildPart(processedPart: ProcessedPart): boolean {
  if (processedPart.kind !== 'data-message') {
    return false;
  }

  const metadata = (
    processedPart.dataMessage as { metadata?: { backgroundChild?: unknown } }
  )?.metadata;

  return metadata?.backgroundChild === true;
}

function isAbortedPart(processedPart: ProcessedPart): boolean {
  return getToolStatusFromPart(processedPart) === 'aborted';
}

export function isSubAgentGroupStopped(group: SubAgentExecutionGroup): boolean {
  return [group.parent, ...group.continuationParents, ...group.children].some(
    (item) => isAbortedPart(item.processedPart),
  );
}

export function resolveSubAgentGroupExecution({
  group,
  getBackgroundExecutionByToolCallId,
}: {
  group: SubAgentExecutionGroup;
  getBackgroundExecutionByToolCallId?: GetBackgroundExecutionByToolCallId;
}): {
  forceParentRunning: boolean;
  isStopped: boolean;
  resultDelivered: boolean;
} {
  const executions = [group.parent, ...group.continuationParents].map(
    (item) => {
      const toolCallId = getToolCallIdFromPart(item.processedPart);

      return toolCallId
        ? getBackgroundExecutionByToolCallId?.(toolCallId)
        : undefined;
    },
  );
  const isStopped = executions.every(
    (execution) => execution?.terminalStatus === 'aborted',
  );

  return {
    forceParentRunning:
      !isStopped && executions.some((execution) => execution?.isRunning),
    isStopped,
    resultDelivered: executions.every((execution) =>
      Boolean(execution?.terminalStatus),
    ),
  };
}

export function isSubAgentGroupRunning(group: SubAgentExecutionGroup): boolean {
  if (isSubAgentGroupStopped(group)) {
    return false;
  }

  return (
    isRunningPart(group.parent.processedPart) ||
    group.continuationParents.some((item) =>
      isRunningPart(item.processedPart),
    ) ||
    group.children.some(
      (item) =>
        isRunningPart(item.processedPart) &&
        !isBackgroundChildPart(item.processedPart),
    )
  );
}

export function canMergeSubAgentContinuation({
  group,
  item,
}: {
  group: SubAgentExecutionGroup;
  item: RenderablePart;
}): boolean {
  if (!group.hasAttachedHITLResponse) {
    return false;
  }

  const groupToolName = getToolNameFromPart(group.parent.processedPart);
  const itemToolName = getToolNameFromPart(item.processedPart);

  return !!groupToolName && !!itemToolName && groupToolName === itemToolName;
}

export function appendSubAgentAssistantText({
  group,
  item,
}: {
  group: SubAgentExecutionGroup;
  item: RenderablePart;
}): void {
  group.children.push(item);
}

export function createRenderablePart({
  processedMessage,
  processedPart,
  noAssistantHeader,
  autoCollapseToolCalls,
}: {
  processedMessage: ProcessedMessage;
  processedPart: ProcessedPart;
  noAssistantHeader: boolean;
  autoCollapseToolCalls: boolean;
}): RenderablePart {
  return {
    processedMessage,
    processedPart,
    noAssistantHeader:
      processedPart.kind === 'data-message' ? true : noAssistantHeader,
    autoCollapseToolCalls,
  };
}

export function splitContinuationMessageParts({
  parts,
}: {
  parts: ProcessedPart[];
}): {
  narrativeParts: ProcessedPart[];
  subAgentResumeParts: ProcessedPart[];
} {
  const visibleParts = parts.filter((part) => part.kind !== 'skip');
  const lastCodeAnalystWriteupKey = getLastCodeAnalystWriteupKey(visibleParts);
  const firstMainAgentTextIndex = visibleParts.findIndex(
    (part) =>
      isAssistantResponsePart(part) &&
      !shouldNestSubAgentAssistantPart({
        processedPart: part,
        lastCodeAnalystWriteupKey,
      }),
  );

  if (firstMainAgentTextIndex < 0) {
    return { narrativeParts: [], subAgentResumeParts: visibleParts };
  }

  const firstSubAgentStartIndex = visibleParts.findIndex(
    (part, index) =>
      index > firstMainAgentTextIndex &&
      isSubAgentPart(part) &&
      isRunningPart(part),
  );

  const narrativeEnd =
    firstSubAgentStartIndex >= 0
      ? firstSubAgentStartIndex
      : visibleParts.length;

  return {
    narrativeParts: visibleParts.slice(firstMainAgentTextIndex, narrativeEnd),
    subAgentResumeParts: [
      ...visibleParts.slice(0, firstMainAgentTextIndex),
      ...visibleParts.slice(narrativeEnd),
    ],
  };
}

export function buildSubAgentSummary(
  children: RenderablePart[],
): string | undefined {
  return buildActivityCountSummary(children);
}
